/**
 * ATR TP / SL + size suggestions for `.VEST` charts (#637).
 *
 * Kept apart from brokerStore (which hides every non-tradovate broker, see
 * brokerVisibility.ts): the Vest broker id is learned here from `broker:list`
 * (platform 'vest', a connected one preferred) and, failing that, from the
 * `venue: 'vest'` rows of `broker:positions`.
 *
 * POLLING. Only while at least one `.VEST` chart with the toggle on is
 * mounted (`watchVestSizing`) and the tab is visible: `broker:perps-sizing`
 * per watched market at once (mount, symbol change, reconnect, broker found,
 * tab shown, config changed) and every 15 s. Nothing is sent for any other
 * chart. Results are relayed to every engine client, so only answers to this
 * tab's own requestIds, for the market asked, while still watched, are kept.
 *
 * CONFIG. `broker:perps-sizing-config` is fetched when watching starts; a
 * broadcast `broker:perps-sizing-config-result` with `changed: true` (risk $
 * set from the phone / overlay) re-requests every watched market.
 */

import { create } from 'zustand';
import { wsService } from '../services/WebSocketService';
import { parseVestSizingResult, sizingMarketOf, type VestSizingEntry } from '../utils/vestSizingLines';

export const VEST_SIZING_POLL_MS = 15_000;
/** A request not answered in this long is forgotten (its late answer is ignored). */
const PENDING_TTL_MS = 60_000;

interface VestSizingState {
  /** The Vest broker every request is sent for; null until one is known. */
  brokerId: string | null;
  /** Last answer per market (upper-case wire symbol). */
  byMarket: Readonly<Record<string, VestSizingEntry>>;
  /** Last sizing config seen (opaque here: only the service interprets it). */
  config: unknown;
  reset: () => void;
}

export const useVestSizingStore = create<VestSizingState>((set) => ({
  brokerId: null,
  byMarket: {},
  config: null,
  reset: () => set({ brokerId: null, byMarket: {}, config: null }),
}));

// --- broker id ----------------------------------------------------------------

interface ListedBroker { id: string; connected: boolean }

let listedVestBrokers: ListedBroker[] = [];
let positionVestBrokerId: string | null = null;

/** Prefer a connected Vest broker from `broker:list`, then any listed one, then one seen in positions. */
export function pickVestBrokerId(listed: readonly ListedBroker[], fromPositions: string | null): string | null {
  return listed.find(b => b.connected)?.id ?? listed[0]?.id ?? fromPositions;
}

function updateBrokerId(): void {
  const next = pickVestBrokerId(listedVestBrokers, positionVestBrokerId);
  if (next === useVestSizingStore.getState().brokerId) return;
  useVestSizingStore.setState({ brokerId: next });
  if (next) {
    requestConfig();
    requestAll();
  }
}

const onBrokerList = (data: { brokers?: unknown }) => {
  if (!data || !Array.isArray(data.brokers)) return;
  const out: ListedBroker[] = [];
  for (const b of data.brokers) {
    if (!b || typeof b !== 'object') continue;
    const r = b as Record<string, unknown>;
    if (r.platform !== 'vest' || typeof r.id !== 'string' || r.id === '') continue;
    out.push({ id: r.id, connected: r.status === 'connected' });
  }
  listedVestBrokers = out;
  updateBrokerId();
};

const onPositions = (data: { brokerId?: unknown; positions?: unknown }) => {
  if (!data || typeof data.brokerId !== 'string' || !Array.isArray(data.positions)) return;
  const isVest = data.positions.some(p => p && typeof p === 'object' && (p as Record<string, unknown>).venue === 'vest');
  if (!isVest || positionVestBrokerId === data.brokerId) return;
  positionVestBrokerId = data.brokerId;
  updateBrokerId();
};

// --- watching + polling -----------------------------------------------------------

/** market -> number of mounted charts watching it. */
const watchers = new Map<string, number>();
/** requestId -> market + when it was sent. */
const pending = new Map<string, { market: string; at: number }>();
let configRequestId: string | null = null;
let seq = 0;
let timer: ReturnType<typeof setInterval> | null = null;
let visibilityBound = false;

function isVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

function canSend(): boolean {
  const ws = wsService as { isConnected?: () => boolean };
  return typeof ws.isConnected === 'function' ? ws.isConnected() : true;
}

function nextRequestId(prefix: string): string {
  seq = (seq + 1) % 1_000_000;
  return `${prefix}-${Date.now().toString(36)}-${seq}`;
}

function prunePending(now: number): void {
  for (const [id, p] of pending) if (now - p.at > PENDING_TTL_MS) pending.delete(id);
}

function requestMarket(market: string): void {
  const brokerId = useVestSizingStore.getState().brokerId;
  if (!brokerId || !isVisible() || !canSend()) return;
  const now = Date.now();
  prunePending(now);
  const requestId = nextRequestId('vsz');
  pending.set(requestId, { market, at: now });
  wsService.emit('broker:perps-sizing', { brokerId, symbol: market, requestId });
}

function requestAll(): void {
  for (const market of watchers.keys()) requestMarket(market);
}

function requestConfig(): void {
  const brokerId = useVestSizingStore.getState().brokerId;
  if (!brokerId || watchers.size === 0 || !isVisible() || !canSend()) return;
  configRequestId = nextRequestId('vszc');
  wsService.emit('broker:perps-sizing-config', { brokerId, requestId: configRequestId });
}

function syncTimer(): void {
  const want = watchers.size > 0 && isVisible();
  if (want && !timer) timer = setInterval(requestAll, VEST_SIZING_POLL_MS);
  if (!want && timer) {
    clearInterval(timer);
    timer = null;
  }
}

const onVisibility = () => {
  syncTimer();
  if (isVisible() && watchers.size > 0) {
    requestConfig();
    requestAll();
  }
};

function bindVisibility(): void {
  if (visibilityBound || typeof document === 'undefined') return;
  visibilityBound = true;
  document.addEventListener('visibilitychange', onVisibility);
}

/**
 * A `.VEST` chart for `market` (`BTC-PERP`) is showing suggestions: request
 * now and every 15 s while the tab is visible. Returns the unwatch.
 */
export function watchVestSizing(market: string): () => void {
  const m = market.trim().toUpperCase();
  if (!m) return () => {};
  bindVisibility();
  const wasEmpty = watchers.size === 0;
  watchers.set(m, (watchers.get(m) ?? 0) + 1);
  if (wasEmpty) requestConfig();
  requestMarket(m);
  syncTimer();
  let done = false;
  return () => {
    if (done) return;
    done = true;
    const n = (watchers.get(m) ?? 0) - 1;
    if (n > 0) watchers.set(m, n);
    else watchers.delete(m);
    syncTimer();
  };
}

// --- results -----------------------------------------------------------------

const onSizingResult = (data: { requestId?: unknown; symbol?: unknown }) => {
  if (!data || typeof data.requestId !== 'string') return;
  const asked = pending.get(data.requestId);
  if (!asked) return; // another client's, or forgotten
  pending.delete(data.requestId);
  if (!watchers.has(asked.market)) return;
  const entry = parseVestSizingResult(data);
  if (!entry) return;
  // A wire / display mismatch (`NQ-PERP` asked, `NDX-USD-PERP` answered) still belongs to the market asked.
  const answered = sizingMarketOf(data);
  const display = typeof (data as { displaySymbol?: unknown }).displaySymbol === 'string'
    ? ((data as { displaySymbol: string }).displaySymbol).trim().toUpperCase() : null;
  if (answered !== asked.market && display !== asked.market) return;
  const stored = { ...entry, market: asked.market } as VestSizingEntry;
  useVestSizingStore.setState(s => ({ byMarket: { ...s.byMarket, [asked.market]: stored } }));
};

const onConfigResult = (data: { requestId?: unknown; success?: unknown; config?: unknown; changed?: unknown }) => {
  if (!data || data.success !== true) return;
  const mine = typeof data.requestId === 'string' && data.requestId === configRequestId;
  if (!mine && data.changed !== true) return;
  if (mine) configRequestId = null;
  useVestSizingStore.setState({ config: data.config ?? null });
  if (data.changed === true) requestAll();
};

const onConnect = () => {
  if (watchers.size === 0) return;
  requestConfig();
  requestAll();
};

let handlersRegistered = false;

/** Must be called before a forced reconnect so the handlers are re-registered on the new socket. */
export function resetVestSizingSocketHandlers(): void {
  handlersRegistered = false;
}

export function initVestSizingSocketHandlers(): void {
  if (handlersRegistered) return;
  handlersRegistered = true;
  wsService.on('broker:list', onBrokerList);
  wsService.on('broker:positions', onPositions);
  wsService.on('broker:perps-sizing-result', onSizingResult);
  wsService.on('broker:perps-sizing-config-result', onConfigResult);
  wsService.on('connect', onConnect);
}

/** Tests only: forget every watcher, pending request, timer and learned broker. */
export function _resetVestSizingForTests(): void {
  watchers.clear();
  pending.clear();
  configRequestId = null;
  listedVestBrokers = [];
  positionVestBrokerId = null;
  if (timer) clearInterval(timer);
  timer = null;
  handlersRegistered = false;
  useVestSizingStore.getState().reset();
}
